import { getCollegeApiFields } from './collegeColumns';

let cachedData = null;
let pendingRequest = null;
const STORAGE_CACHE_KEY = 'college_scorecard_data_cache_v2';
const STORAGE_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const CACHE_DATABASE_NAME = 'maicolleges-cache';
const CACHE_STORE_NAME = 'datasets';
const CACHE_DATABASE_VERSION = 1;
const SCORECARD_CACHE_ID = 'college-scorecard-v3';

function openCacheDatabase() {
  if (typeof indexedDB === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {
    const request = indexedDB.open(CACHE_DATABASE_NAME, CACHE_DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CACHE_STORE_NAME)) {
        database.createObjectStore(CACHE_STORE_NAME, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
}

async function readIndexedDbCache() {
  const database = await openCacheDatabase();
  if (!database) return null;

  return new Promise((resolve) => {
    const transaction = database.transaction(CACHE_STORE_NAME, 'readonly');
    const request = transaction.objectStore(CACHE_STORE_NAME).get(SCORECARD_CACHE_ID);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => resolve(null);
    transaction.oncomplete = () => database.close();
    transaction.onerror = () => database.close();
  });
}

async function writeIndexedDbCache(data) {
  const database = await openCacheDatabase();
  if (!database) return false;

  return new Promise((resolve) => {
    const transaction = database.transaction(CACHE_STORE_NAME, 'readwrite');
    transaction.objectStore(CACHE_STORE_NAME).put({
      id: SCORECARD_CACHE_ID,
      savedAt: Date.now(),
      data,
    });
    transaction.oncomplete = () => {
      database.close();
      resolve(true);
    };
    transaction.onerror = () => {
      database.close();
      resolve(false);
    };
    transaction.onabort = () => {
      database.close();
      resolve(false);
    };
  });
}

function readLegacyCache() {
  if (typeof localStorage === 'undefined') return null;

  try {
    const raw = localStorage.getItem(STORAGE_CACHE_KEY);
    if (!raw) return null;

    const cached = JSON.parse(raw);
    if (cached?.savedAt && Array.isArray(cached?.data?.results)) return cached;
  } catch {
    // Ignore corrupt cache and fetch fresh data.
  }

  return null;
}

async function readStoredCache() {
  const indexedCache = await readIndexedDbCache();
  const cached = indexedCache || readLegacyCache();
  if (!cached?.savedAt || !Array.isArray(cached?.data?.results)) return null;

  if (!indexedCache) {
    const migrated = await writeIndexedDbCache(cached.data);
    if (migrated) {
      try { localStorage.removeItem(STORAGE_CACHE_KEY); } catch { /* ignore */ }
    }
  }

  return {
    data: cached.data,
    isFresh: Date.now() - cached.savedAt < STORAGE_CACHE_TTL_MS,
  };
}

async function saveStoredCache(data) {
  const savedToIndexedDb = await writeIndexedDbCache(data);
  if (savedToIndexedDb) {
    try { localStorage.removeItem(STORAGE_CACHE_KEY); } catch { /* ignore */ }
    return;
  }

  if (typeof localStorage === 'undefined') return;

  try {
    localStorage.setItem(STORAGE_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      data,
    }));
  } catch {
    // Ignore quota errors; in-memory cache still helps during this tab session.
  }
}

export const fetchCollegeData = async () => {
  // Return cached data if available (avoids re-fetching on component re-mount)
  if (cachedData) {
    console.log(`Returning cached data (${cachedData.results.length} colleges)`);
    return cachedData;
  }

  const storedCache = await readStoredCache();
  if (storedCache) {
    cachedData = storedCache.data;
    console.log(`Returning stored college data (${cachedData.results.length} colleges)`);
    if (!storedCache.isFresh && !pendingRequest) {
      pendingRequest = fetchCollegeDataFromApi().finally(() => {
        pendingRequest = null;
      });
    }
    return cachedData;
  }

  if (pendingRequest) {
    return pendingRequest;
  }

  pendingRequest = fetchCollegeDataFromApi();
  try {
    return await pendingRequest;
  } finally {
    pendingRequest = null;
  }
};

const fetchCollegeDataFromApi = async () => {
  const url = "https://api.data.gov/ed/collegescorecard/v1/schools";
  const apiKey = process.env.REACT_APP_COLLEGE_API_KEY;
  const fields = getCollegeApiFields();
  const perPage = 100; // Maximum allowed by the API

  try {
    // Fetch first page to get total count
    const response = await fetch(`${url}?api_key=${apiKey}&fields=${fields}&per_page=${perPage}&page=0`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    console.log(`Fetched ${data.results.length} colleges from page 0. Total available: ${data.metadata.total}`);

    const totalPages = Math.ceil(data.metadata.total / perPage);
    const allResults = [...data.results];

    // Fetch remaining pages in parallel batches of 10 (avoids API rate-limiting)
    const BATCH_SIZE = 10;
    for (let batchStart = 1; batchStart < totalPages; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, totalPages);
      const batchPromises = [];

      for (let page = batchStart; page < batchEnd; page++) {
        batchPromises.push(
          fetch(`${url}?api_key=${apiKey}&fields=${fields}&per_page=${perPage}&page=${page}`)
            .then(res => res.ok ? res.json() : null)
            .then(pageData => pageData ? pageData.results : [])
            .catch(err => {
              console.warn(`Failed to fetch page ${page}:`, err);
              return [];
            })
        );
      }

      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(results => allResults.push(...results));
      console.log(`Fetched batch (pages ${batchStart}-${batchEnd - 1}), total so far: ${allResults.length}`);
    }

    console.log(`Total colleges fetched: ${allResults.length}`);

    // Cache the result for future calls
    cachedData = { ...data, results: allResults };
    await saveStoredCache(cachedData);
    return cachedData;
  } catch (error) {
    console.error("Error fetching college data:", error);
  }
};

// latest.cost.avg_net_price.overall
// "latest.student.FAFSA_applications":
// latest.student.demographics.women
